import React, {
    useEffect,
    useState
} from "react";

import {
    Link,
    useParams
} from "react-router-dom";

import {
    ArrowLeft,
    ArrowRight,
    Calendar,
    Clock,
    BookOpen,
    Tag,
    Leaf
} from "lucide-react";

import { Helmet } from "react-helmet-async";


// const API_URL ="http://localhost:8483";
const API_URL ="https://www.ayushvatika.com";


interface Blog {

    id: number;

    title: string;

    slug: string;

    category: string;

    excerpt: string;

    content: string;

    featured_image?: string;

    image_alt?: string;

    seo_title?: string;

    meta_description?: string;

    focus_keyword?: string;

    short_tail_keywords?: string;

    long_tail_keywords?: string;

    author?: string;

    status: string;

    published_at?: string;

    created_at: string;

}

interface BlogResponse {

    success: boolean;

    blog: Blog;

}

interface BlogsResponse {

    success: boolean;

    blogs: Blog[];

}


const BlogContent: React.FC = () => {

    const {
        slug
    } = useParams<{
        slug: string;
    }>();


    const [blog, setBlog] =
        useState<Blog | null>(null);


    const [relatedBlogs, setRelatedBlogs] =
        useState<Blog[]>([]);


    const [loading, setLoading] =
        useState(true);


    const [error, setError] =
        useState(false);


    // ========================================================
    // FETCH BLOG
    // ========================================================

    useEffect(() => {

        const fetchBlog = async () => {

            if (!slug) {
                return;
            }


            try {

                setLoading(true);

                setError(false);


                const response =
                    await fetch(
                        `${API_URL}/api/blogs/slug/${slug}`
                    );


                const data =
                    await response.json() as BlogResponse;


                if (
                    !response.ok ||
                    !data.success
                ) {

                    setError(true);

                    return;

                }


                setBlog(data.blog);


                // ==================================================
                // RELATED BLOGS
                // ==================================================

                try {

                    const allResponse =
                        await fetch(
                            `${API_URL}/api/blogs`
                        );


                    const allData =
                        await allResponse.json() as BlogsResponse;


                    if (
                        allResponse.ok &&
                        allData.success
                    ) {

                        const related =
                            allData.blogs
                                .filter(
                                    (item: Blog) =>
                                        item.slug !==
                                        data.blog.slug
                                )
                                .filter(
                                    (item: Blog) =>
                                        item.category ===
                                        data.blog.category
                                )
                                .slice(0, 3);


                        // If same category doesn't
                        // have enough blogs

                        if (
                            related.length < 3
                        ) {

                            const additional =
                                allData.blogs
                                    .filter(
                                        (item: Blog) =>
                                            item.slug !==
                                            data.blog.slug
                                    )
                                    .filter(
                                        (item: Blog) =>
                                            !related.some(
                                                (rel: Blog) =>
                                                    rel.id ===
                                                    item.id
                                            )
                                    )
                                    .slice(
                                        0,
                                        3 - related.length
                                    );


                            setRelatedBlogs([
                                ...related,
                                ...additional
                            ]);

                        } else {

                            setRelatedBlogs(
                                related
                            );

                        }

                    }

                } catch (error) {

                    console.error(
                        "Related blogs error:",
                        error
                    );

                }

            } catch (error) {

                console.error(
                    "Blog detail error:",
                    error
                );

                setError(true);

            } finally {

                setLoading(false);

            }

        };


        fetchBlog();

    }, [slug]);


    // ========================================================
    // DATE
    // ========================================================

    const formatDate = (
        date?: string
    ) => {

        if (!date) {
            return "";
        }


        return new Date(
            date
        ).toLocaleDateString(
            "en-IN",
            {
                day: "numeric",
                month: "long",
                year: "numeric"
            }
        );

    };


    // ========================================================
    // IMAGE
    // ========================================================

    const getImageUrl = (
        image?: string
    ) => {

        if (!image) {

            return "/placeholder-blog.webp";

        }


        if (
            image.startsWith("http")
        ) {

            return image;

        }


        return `${API_URL}${image}`;

    };


    // ========================================================
    // LOADING
    // ========================================================

    if (loading) {

        return (

            <main
                className="
                    min-h-[70vh]
                    flex
                    items-center
                    justify-center
                    px-4
                "
            >

                <div
                    className="
                        text-center
                    "
                >

                    <div
                        className="
                            w-12
                            h-12
                            border-4
                            border-green-100
                            border-t-green-700
                            rounded-full
                            animate-spin
                            mx-auto
                        "
                    />

                    <p
                        className="
                            mt-4
                            text-gray-500
                        "
                    >
                        Loading article...
                    </p>

                </div>

            </main>

        );

    }


    // ========================================================
    // NOT FOUND
    // ========================================================

    if (
        error ||
        !blog
    ) {

        return (

            <main
                className="
                    min-h-[70vh]
                    flex
                    items-center
                    justify-center
                    px-4
                "
            >

                <div
                    className="
                        text-center
                        max-w-md
                    "
                >

                    <Leaf
                        className="
                            w-14
                            h-14
                            mx-auto
                            text-green-700
                        "
                    />


                    <h1
                        className="
                            text-3xl
                            font-bold
                            text-gray-900
                            mt-5
                        "
                    >
                        Article Not Found
                    </h1>


                    <p
                        className="
                            text-gray-500
                            mt-3
                        "
                    >
                        The Ayurveda article you're
                        looking for could not be found.
                    </p>


                    <Link
                        to="/blogs"
                        className="
                            inline-flex
                            items-center
                            gap-2
                            mt-6
                            bg-green-700
                            hover:bg-green-800
                            text-white
                            px-6
                            py-3
                            rounded-full
                            font-semibold
                        "
                    >

                        <ArrowLeft
                            className="
                                w-4
                                h-4
                            "
                        />

                        Back to Ayurveda Blog

                    </Link>

                </div>

            </main>

        );

    }


    // ========================================================
    // KEYWORDS
    // ========================================================

    const keywords = [

        ...(blog.short_tail_keywords
            ? blog.short_tail_keywords
                .split(",")
                .map(item => item.trim())
            : []),

        ...(blog.long_tail_keywords
            ? blog.long_tail_keywords
                .split(",")
                .map(item => item.trim())
            : [])

    ]
        .filter(Boolean)
        .slice(0, 12);


    return (

        <>

            {/* ==================================================
                SEO
            ================================================== */}

            <Helmet>

                <title>
                    {
                        blog.seo_title ||
                        blog.title
                    }
                    {" | Ayushvatika"}
                </title>


                <meta
                    name="description"
                    content={
                        blog.meta_description ||
                        blog.excerpt
                    }
                />


                {blog.focus_keyword && (

                    <meta
                        name="keywords"
                        content={[
                            blog.focus_keyword,
                            ...keywords
                        ].join(", ")}
                    />

                )}


                <link
                    rel="canonical"
                    href={`https://www.ayushvatika.com/blogs/${blog.slug}`}
                />


                {/* Open Graph */}

                <meta
                    property="og:title"
                    content={
                        blog.seo_title ||
                        blog.title
                    }
                />


                <meta
                    property="og:description"
                    content={
                        blog.meta_description ||
                        blog.excerpt
                    }
                />


                <meta
                    property="og:type"
                    content="article"
                />


                <meta
                    property="og:url"
                    content={
                        `https://www.ayushvatika.com/blogs/${blog.slug}`
                    }
                />


                {blog.featured_image && (

                    <meta
                        property="og:image"
                        content={
                            getImageUrl(
                                blog.featured_image
                            )
                        }
                    />

                )}

            </Helmet>



            <main
                className="
                    min-h-screen
                    bg-white
                "
            >


                {/* ==================================================
                    BREADCRUMB
                ================================================== */}

                <div
                    className="
                        max-w-7xl
                        mx-auto
                        px-4
                        sm:px-6
                        lg:px-8
                        pt-6
                    "
                >

                    <nav
                        aria-label="Breadcrumb"
                        className="
                            flex
                            items-center
                            flex-wrap
                            gap-2
                            text-xs
                            sm:text-sm
                            text-gray-500
                        "
                    >

                        <Link
                            to="/"
                            className="
                                hover:text-green-700
                            "
                        >
                            Home
                        </Link>


                        <span>/</span>


                        <Link
                            to="/blogs"
                            className="
                                hover:text-green-700
                            "
                        >
                            Ayurveda Blog
                        </Link>


                        <span>/</span>


                        <span
                            className="
                                text-gray-700
                                line-clamp-1
                            "
                        >
                            {blog.title}
                        </span>

                    </nav>

                </div>



                {/* ==================================================
                    ARTICLE HEADER
                ================================================== */}

                <header
                    className="
                        max-w-5xl
                        mx-auto
                        px-4
                        sm:px-6
                        lg:px-8
                        pt-8
                        sm:pt-12
                        text-center
                    "
                >

                    <div
                        className="
                            inline-flex
                            items-center
                            gap-2
                            bg-green-50
                            border
                            border-green-100
                            text-green-800
                            px-4
                            py-1.5
                            rounded-full
                            text-xs
                            font-bold
                            uppercase
                            tracking-wider
                        "
                    >

                        <Tag
                            className="
                                w-3.5
                                h-3.5
                            "
                        />

                        {blog.category ||
                            "Ayurveda"}

                    </div>



                    <h1
                        className="
                            text-3xl
                            sm:text-4xl
                            lg:text-5xl
                            font-extrabold
                            text-[#19352a]
                            leading-tight
                            mt-5
                        "
                    >

                        {blog.title}

                    </h1>



                    <p
                        className="
                            max-w-3xl
                            mx-auto
                            text-gray-600
                            text-base
                            sm:text-lg
                            leading-7
                            mt-5
                        "
                    >

                        {blog.excerpt}

                    </p>



                    {/* META */}

                    <div
                        className="
                            flex
                            justify-center
                            items-center
                            flex-wrap
                            gap-5
                            mt-6
                            text-sm
                            text-gray-500
                        "
                    >

                        <span
                            className="
                                inline-flex
                                items-center
                                gap-2
                            "
                        >

                            <Calendar
                                className="
                                    w-4
                                    h-4
                                "
                            />

                            {formatDate(
                                blog.published_at
                            )}

                        </span>


                        <span
                            className="
                                inline-flex
                                items-center
                                gap-2
                            "
                        >

                            <Clock
                                className="
                                    w-4
                                    h-4
                                "
                            />

                            6 min read

                        </span>


                        <span
                            className="
                                inline-flex
                                items-center
                                gap-2
                            "
                        >

                            <BookOpen
                                className="
                                    w-4
                                    h-4
                                "
                            />

                            Ayushvatika

                        </span>

                    </div>

                </header>



                {/* ==================================================
                    HERO IMAGE
                ================================================== */}

                <div
                    className="
                        max-w-6xl
                        mx-auto
                        px-4
                        sm:px-6
                        lg:px-8
                        mt-8
                        sm:mt-10
                    "
                >

                    <div
                        className="
                            aspect-[3/2]
                            rounded-2xl
                            sm:rounded-3xl
                            overflow-hidden
                            shadow-lg
                            bg-gray-100
                        "
                    >

                        <img
                            src={
                                getImageUrl(
                                    blog.featured_image
                                )
                            }
                            alt={
                                blog.image_alt ||
                                blog.title
                            }
                            className="
                                w-full
                                h-full
                                object-cover
                            "
                        />

                    </div>

                </div>



                {/* ==================================================
                    ARTICLE CONTENT
                ================================================== */}

                <article
                    className="
                        max-w-3xl
                        mx-auto
                        px-4
                        sm:px-6
                        lg:px-0
                        py-10
                        sm:py-14
                    "
                >

                    {/* INTRO */}

                    <div
                        className="
                            text-lg
                            sm:text-xl
                            text-gray-700
                            leading-8
                            font-medium
                            border-l-4
                            border-green-600
                            pl-5
                            mb-10
                        "
                    >

                        {blog.excerpt}

                    </div>



                    {/* ==================================================
                        DYNAMIC HTML CONTENT
                    ================================================== */}

                    <div
                        className="
                            blog-content
                            text-gray-700
                        "
                        dangerouslySetInnerHTML={{
                            __html:
                                blog.content
                        }}
                    />



                    {/* ==================================================
                        KEYWORDS
                    ================================================== */}

                    {keywords.length > 0 && (

                        <section
                            className="
                                mt-10
                                pt-7
                                border-t
                                border-gray-100
                            "
                        >

                            <p
                                className="
                                    text-xs
                                    font-bold
                                    uppercase
                                    tracking-wider
                                    text-gray-500
                                    mb-3
                                "
                            >
                                Related Ayurveda Topics
                            </p>


                            <div
                                className="
                                    flex
                                    flex-wrap
                                    gap-2
                                "
                            >

                                {keywords.map(
                                    keyword => (

                                        <span
                                            key={keyword}
                                            className="
                                                px-3
                                                py-1.5
                                                rounded-full
                                                text-xs
                                                bg-green-50
                                                text-green-700
                                                border
                                                border-green-100
                                            "
                                        >

                                            {keyword}

                                        </span>

                                    )
                                )}

                            </div>

                        </section>

                    )}



                    {/* ==================================================
                        DISCLAIMER
                    ================================================== */}

                    <div
                        className="
                            mt-8
                            text-xs
                            text-gray-400
                            leading-5
                        "
                    >

                        <strong>
                            Disclaimer:
                        </strong>{" "}

                        This article is provided for
                        general educational and wellness
                        information. Ayurvedic herbs and
                        traditional practices may not be
                        suitable for everyone. This content
                        should not be considered a substitute
                        for professional medical advice,
                        diagnosis or treatment.

                    </div>

                </article>



                {/* ==================================================
                    RELATED BLOGS
                ================================================== */}

                {relatedBlogs.length > 0 && (

                    <section
                        className="
                            bg-[#f7fbf5]
                            border-t
                            border-gray-100
                            py-12
                            sm:py-14
                        "
                    >

                        <div
                            className="
                                max-w-7xl
                                mx-auto
                                px-4
                                sm:px-6
                                lg:px-8
                            "
                        >

                            <div
                                className="
                                    flex
                                    items-end
                                    justify-between
                                    gap-4
                                    mb-7
                                "
                            >

                                <div>

                                    <p
                                        className="
                                            text-green-700
                                            text-xs
                                            font-bold
                                            uppercase
                                            tracking-widest
                                        "
                                    >
                                        Keep Reading
                                    </p>


                                    <h2
                                        className="
                                            text-2xl
                                            sm:text-3xl
                                            font-bold
                                            text-[#19352a]
                                            mt-1
                                        "
                                    >
                                        Related Ayurveda
                                        Articles
                                    </h2>

                                </div>


                                <Link
                                    to="/blog"
                                    className="
                                        hidden
                                        sm:inline-flex
                                        items-center
                                        gap-2
                                        text-green-700
                                        font-semibold
                                        text-sm
                                    "
                                >

                                    All Blogs

                                    <ArrowRight
                                        className="
                                            w-4
                                            h-4
                                        "
                                    />

                                </Link>

                            </div>



                            <div
                                className="
                                    grid
                                    grid-cols-1
                                    md:grid-cols-3
                                    gap-5
                                "
                            >

                                {relatedBlogs.map(
                                    related => (

                                        <Link
                                            key={
                                                related.id
                                            }
                                            to={
                                                `/blogs/${related.slug}`
                                            }
                                            className="
                                                group
                                                bg-white
                                                rounded-2xl
                                                overflow-hidden
                                                border
                                                border-gray-200
                                                shadow-sm
                                                hover:shadow-lg
                                                transition-all
                                            "
                                        >

                                            <div
                                                className="
                                                    aspect-[3/2]
                                                    overflow-hidden
                                                    bg-gray-100
                                                "
                                            >

                                                <img
                                                    src={
                                                        getImageUrl(
                                                            related.featured_image
                                                        )
                                                    }
                                                    alt={
                                                        related.image_alt ||
                                                        related.title
                                                    }
                                                    loading="lazy"
                                                    className="
                                                        w-full
                                                        h-full
                                                        object-cover
                                                        group-hover:scale-105
                                                        transition-transform
                                                        duration-500
                                                    "
                                                />

                                            </div>


                                            <div
                                                className="
                                                    p-5
                                                "
                                            >

                                                <span
                                                    className="
                                                        text-[10px]
                                                        uppercase
                                                        tracking-wider
                                                        font-bold
                                                        text-green-700
                                                    "
                                                >

                                                    {
                                                        related.category
                                                    }

                                                </span>


                                                <h3
                                                    className="
                                                        text-base
                                                        font-bold
                                                        text-[#19352a]
                                                        mt-2
                                                        line-clamp-2
                                                        group-hover:text-green-700
                                                    "
                                                >

                                                    {
                                                        related.title
                                                    }

                                                </h3>


                                                <div
                                                    className="
                                                        flex
                                                        items-center
                                                        gap-1.5
                                                        text-xs
                                                        text-gray-400
                                                        mt-3
                                                    "
                                                >

                                                    <Clock
                                                        className="
                                                            w-3.5
                                                            h-3.5
                                                        "
                                                    />

                                                    6 min read

                                                </div>

                                            </div>

                                        </Link>

                                    )
                                )}

                            </div>

                        </div>

                    </section>

                )}



                {/* ==================================================
                    BACK
                ================================================== */}

                <div
                    className="
                        flex
                        justify-center
                        py-10
                    "
                >

                    <Link
                        to="/blogs"
                        className="
                            inline-flex
                            items-center
                            gap-2
                            border
                            border-green-700
                            text-green-700
                            hover:bg-green-700
                            hover:text-white
                            px-6
                            py-3
                            rounded-full
                            font-semibold
                            text-sm
                            transition-all
                        "
                    >

                        <ArrowLeft
                            className="
                                w-4
                                h-4
                            "
                        />

                        Back to All Blogs

                    </Link>

                </div>

            </main>

        </>

    );

};


export default BlogContent;